<template>
    <section class="container py-4">
        <div class="mb-4 d-flex justify-content-between align-items-end">
            <div>
                <label>Status:</label>
                <select class="form-select" v-model="options.filter.status_type">
                    <option v-for="s in status_types" :key="s.id" :value="s.id">
                        {{ s.name }}
                    </option>
                </select>
            </div>
            <button
                class="btn btn-primary"
                @click="editItem(0)"
            >
                Add New {{ label }}
            </button>
        </div>

        <div class="mb-3">
            <h5 class="form-label fw-bold">{{ settings?.title }}</h5>
            <p v-html="settings?.content"></p>
        </div>

        <div class="table-responsive mt-3">
            <table class="table table-bordered table-striped">
                <thead>
                <tr>
                    <th v-for="(header, i) in headers" :key="i">{{ header.text }}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in items" :key="item.id">
                    <td v-for="(header, i) in headers" :key="i">
                        <template v-if="header.value === 'last_response'">
                                <span class="text-primary clickable" style="cursor: pointer;"
                                      @click="editItem(item.id)">
                                    {{ item.last_response }}
                                </span>
                        </template>

                        <template v-else-if="header.value === 'priority'">
                            <span
                                class="tag-label"
                                :style="{ color: item?.priority_color, borderColor: item?.priority_color }"
                            >
                                {{ item.priority }}
                            </span>
                        </template>

                        <template v-else-if="header.value === 'status'">
                            <span
                                class="tag-label"
                                :style="{ color: item?.status_color, borderColor: item?.status_color }"
                            >
                                {{ item.status }}
                            </span>
                        </template>

                        <template v-else>
                            <span v-html="item[header.value]"></span>
                        </template>
                    </td>
                </tr>
                <tr v-if="items.length === 0">
                    <td :colspan="headers.length" class="text-center fw-bold py-3">
                        No Records Found
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div class="d-flex justify-content-center mt-4">
            <pagination
                v-model="options.page"
                :records="count"
                :per-page="options.itemsPerPage"
                @paginate="loadData"
                :options="{
                    chunksNavigation: 'scroll',
                    edgeNavigation: true
                }"
            />
        </div>

        <TicketComponent
            v-bind.sync="itemEdit"
            v-on:reload="loadData"
            :url="url"
            :settings="settings"
            :title="label"
            :categories="categories"
            :priorities="priorities"
        />
    </section>
</template>

<script>
import ConfirmDialog from "../dialogs/ConfirmDialog.vue";
import TicketComponent from "./TicketComponent.vue";
import Pagination from "vue-pagination-2";
import TicketModal from "../support-desk/modals/TicketModal.vue";

export default {
    name: "QueryDeskComponent",
    components: {TicketModal, TicketComponent, Pagination, ConfirmDialog},
    data: () => ({
        isLoading: false,
        items: [],
        count: 0,
        categories: [],
        priorities: [],
        statuses: [],
        support_agents: [],
        primary_sort: [],
        sort_date: [],
        order_by: [],
        status_types: [],
        itemsInDb: 0,
        itemEdit: {
            isActive: false,
            id: 0
        },
        options: {
            itemsPerPage: 30,
            page: 1,
            filter: {
                status_type: 'open',
            }
        },
        footerProps: {
            itemsPerPageOptions: [15, 30, 50]
        },
        settings: {},
        url: "api/v1/query-desk/tickets",
    }),
    computed: {
        headers() {
            return [
                {text: "Last Replier", value: "last_response"},
                {text: "Subject", value: "subject"},
                {text: "Priority", value: "priority"},
                {text: "Status", value: "status"},
                {text: "Category", value: "category"},
                {text: "Due Date", value: "due_date"},
                {text: "Last Update", value: "updated_at"},
            ];
        },
        title() {
            return this.settings?.module_name ?? 'Tickets';
        },
        label() {
            return this.settings?.form_label ?? 'Ticket';
        }
    },
    mounted() {
        this.loadSettings();
        this.loadOptions();
        this.loadData();
    },
    watch: {
        'options.filter.status_type': function () {
            this.loadData();
        }
    },
    methods: {
        loadData() {
            this.isLoading = true;

            this.$api.get(this.url, {params: this.options}).then(response => {
                if (response.data.data) {
                    this.itemsInDb = response.data.meta.total;
                    this.count = response.data.meta.total;
                    this.page = response.data.meta?.current_page;
                    this.items = response.data.data;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadSettings() {
            this.isLoading = true;

            this.$api.get('api/v1/admin/settings/support_desk').then(response => {
                if (response.data[0]?.value) {
                    this.settings = response.data[0]?.value;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadOptions() {
            this.isLoading = true;

            this.$api.get(this.url + '/options').then(response => {
                if (response.data) {
                    this.categories = response.data.categories;
                    this.priorities = response.data.priorities;
                    this.support_agents = response.data.support_agents;
                    this.statuses = response.data.statuses;
                    this.primary_sort = response.data.primary_sort;
                    this.sort_date = response.data.sort_date;
                    this.order_by = response.data.order_by;
                    this.status_types = response.data.status_types;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        editItem(id) {
            this.itemEdit.id = id;
            this.itemEdit.isActive = true;
        },
        deleteItemConfirm() {
            this.isLoading = true;

            this.$api.delete(this.url + '/' + this.itemDelete.id).then(() => {
                this.loadData();
            }).finally(() => {
                this.isLoading = false;
                this.itemDelete.isActive = false;
            });
        }
    }
}
</script>

<style scoped>
.btn {
    padding: 10px 20px;
}

.table-responsive {
    background: #f6f6f6;
}

.tag-label {
    display: inline-block;
    padding: 0.25rem 0.65rem;
    border: 1px solid;
    border-radius: 50px;
    font-weight: 500;
    font-size: 0.85rem;
    background-color: white;
    text-align: center;
    min-width: 80px;
}

table thead {
    border-bottom: 3px solid #c6c6c6;
}

table th {
    font-weight: 500;
}

.text-primary {
    color: rgb(79 80 203) !important;
}

.table td {
    text-align: left;
}
</style>
